import { useEffect, useMemo, useState } from 'react';
import { DatePicker, Select, Space, Tag, Tooltip } from 'antd';
import { useUrlState, oneOf, dayParam } from '../../lib/urlState';
import {
  RANGE_PRESETS,
  elapsedSince,
  formatDuration,
  rangeFor,
  type RangePreset,
  type StatsRangeParams,
} from './listenerStats';

/** Re-renders every `ms` so live session timers tick (no refetch, no DB write). */
export function useNow(ms = 15_000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

/**
 * Date-range state for the stats views. The range is recomputed only when the
 * preset/custom days change — not on every render — so query keys stay stable.
 */
export function useStatsRange(initial: RangePreset = 'today') {
  const [preset, setPreset] = useState<RangePreset>(initial);
  const [custom, setCustom] = useState({ from: '', to: '' });
  const range = useMemo<StatsRangeParams | null>(
    () => rangeFor(preset, new Date(), custom),
    [preset, custom],
  );
  return { preset, setPreset, custom, setCustom, range };
}

/**
 * The same date-range state, kept in the URL (`?range=last7` or
 * `?range=custom&rangeFrom=YYYY-MM-DD&rangeTo=YYYY-MM-DD`) so the period an
 * admin picked survives Back, refresh and a shared link. Same shape as
 * useStatsRange, so a page swaps one for the other without touching its UI.
 */
export function useUrlStatsRange(initial: RangePreset = 'today') {
  const { get, set } = useUrlState();
  const preset = oneOf(get('range'), RANGE_PRESETS.map((p) => p.value), initial) as RangePreset;
  const from = dayParam(get('rangeFrom'));
  const to = dayParam(get('rangeTo'));
  const custom = useMemo(() => ({ from, to }), [from, to]);
  const range = useMemo<StatsRangeParams | null>(() => rangeFor(preset, new Date(), custom), [preset, custom]);
  const setPreset = (p: RangePreset) =>
    set(p === 'custom' ? { range: p } : { range: p === initial ? null : p, rangeFrom: null, rangeTo: null });
  const setCustom = (c: { from: string; to: string }) =>
    set({ range: 'custom', rangeFrom: c.from || null, rangeTo: c.to || null });
  return { preset, setPreset, custom, setCustom, range };
}

export function StatsRangeFilter({
  preset,
  setPreset,
  setCustom,
  custom,
}: {
  preset: RangePreset;
  setPreset: (p: RangePreset) => void;
  setCustom: (c: { from: string; to: string }) => void;
  /** When given, the picker shows the current custom range (URL-backed pages). */
  custom?: { from: string; to: string };
}) {
  return (
    <Space wrap>
      <Select<RangePreset>
        value={preset}
        onChange={setPreset}
        options={RANGE_PRESETS}
        style={{ minWidth: 150 }}
        aria-label="Date range"
      />
      {preset === 'custom' && (
        <DatePicker.RangePicker
          key={custom ? `${custom.from}|${custom.to}` : 'uncontrolled'}
          onChange={(_dates, [from, to]) => setCustom({ from: from ?? '', to: to ?? '' })}
        />
      )}
    </Space>
  );
}

/** Logged In / Logged Out — authenticated app session (NOT availability). */
export function LoginStatusTag({ loggedIn }: { loggedIn: boolean | null | undefined }) {
  if (loggedIn === undefined) return <>—</>;
  if (loggedIn === null) {
    return (
      <Tooltip title="No login recorded since session tracking was enabled. Existing sessions are picked up at their next token refresh.">
        <Tag>No data</Tag>
      </Tooltip>
    );
  }
  return loggedIn ? <Tag color="blue">Logged in</Tag> : <Tag>Logged out</Tag>;
}

/** Online / Offline — available to receive calls right now (Redis presence). */
/**
 * Online status as the buddy card sees it. Online = callable: the app is open
 * (screen on), or the phone is locked but still reachable through push until
 * `reachableUntil`. Pass `screenOn` to tell the two apart.
 */
export function OnlineStatusTag({
  online,
  screenOn,
  reachableUntil,
}: {
  online: boolean | undefined;
  screenOn?: boolean;
  reachableUntil?: string | null;
}) {
  if (online === undefined) return <>—</>;
  if (!online) return <Tag>Offline</Tag>;
  if (screenOn === false) {
    const until = reachableUntil ? new Date(reachableUntil) : null;
    return (
      <Tooltip
        title={
          until
            ? `Phone locked / app in background — still callable through push until ${until.toLocaleTimeString()}`
            : 'Phone locked / app in background — still callable through push'
        }
      >
        <Tag color="gold">Online · phone locked</Tag>
      </Tooltip>
    );
  }
  return <Tag color="success">{screenOn ? 'Online · screen on' : 'Online'}</Tag>;
}

/**
 * The listener's "Online today" — the same numbers the listener's own app card
 * shows, ticking locally between refreshes: online (callable) time grows while
 * the listener is online, including a locked phone; screen-on time grows only
 * while the app is open (the earning-stage basis).
 */
export function OnlineTodayCard({
  online,
  fetchedAt,
  now,
}: {
  online: {
    onlineNow: boolean;
    screenOn?: boolean;
    reachableUntil?: string | null;
    currentOnlineSince: string | null;
    todayOnlineSeconds: number;
    todayScreenOnSeconds?: number;
  };
  /** When the data was fetched (ms) — the local tick runs from here. */
  fetchedAt: number;
  now: number;
}) {
  const sinceFetch = Math.max(0, Math.floor((now - fetchedAt) / 1000));
  const onlineSecs = online.todayOnlineSeconds + (online.onlineNow ? sinceFetch : 0);
  const screenSecs =
    online.todayScreenOnSeconds === undefined
      ? undefined
      : online.todayScreenOnSeconds + (online.onlineNow && online.screenOn ? sinceFetch : 0);
  return (
    <div data-testid="online-today-card" style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
      <div>
        <div style={{ opacity: 0.65, fontSize: 12 }}>Status</div>
        <OnlineStatusTag online={online.onlineNow} screenOn={online.screenOn} reachableUntil={online.reachableUntil} />
      </div>
      <div>
        <div style={{ opacity: 0.65, fontSize: 12 }}>Online today</div>
        <div data-testid="online-today-seconds" style={{ fontSize: 20, fontWeight: 600 }}>{formatDuration(onlineSecs)}</div>
      </div>
      <div>
        <Tooltip title="App open with a live heartbeat — only this counts toward online-earning levels.">
          <div style={{ opacity: 0.65, fontSize: 12 }}>Screen on today</div>
        </Tooltip>
        <div data-testid="screen-on-today-seconds" style={{ fontSize: 20, fontWeight: 600 }}>{formatDuration(screenSecs)}</div>
      </div>
      {online.onlineNow && online.screenOn === false && online.reachableUntil && (
        <div>
          <div style={{ opacity: 0.65, fontSize: 12 }}>Reachable until</div>
          <div style={{ fontSize: 16 }}>{new Date(online.reachableUntil).toLocaleTimeString()}</div>
        </div>
      )}
    </div>
  );
}

/** A current session's running length, ticking client-side from `since`. */
export function LiveDuration({ since, now }: { since: string | null | undefined; now: number }) {
  const secs = elapsedSince(since, now);
  if (secs === null) return <>—</>;
  return (
    <Tooltip title={`Since ${new Date(since!).toLocaleString()}`}>
      <span>{formatDuration(secs)}</span>
    </Tooltip>
  );
}
